import React from "react";
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowId,
} from "@mui/x-data-grid";

const stubApi = {} as GridRenderCellParams["api"];

function asReactNode(v: unknown): React.ReactNode {
  if (v == null || v === "") return "–";
  if (React.isValidElement(v)) return v;
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

/**
 * Resolves the same visible cell content as the Data Grid would (renderCell → valueGetter → valueFormatter → raw).
 */
export function gridColumnToCardContent<T extends Record<string, unknown>>(
  col: GridColDef,
  row: T,
  rowId: GridRowId,
): React.ReactNode {
  const raw = row[col.field as keyof T];

  if (col.renderCell) {
    const params = {
      id: rowId,
      field: col.field,
      row,
      colDef: col,
      value: raw,
      api: stubApi,
      hasFocus: false,
      tabIndex: -1,
    } as GridRenderCellParams<T, unknown, T>;
    try {
      return col.renderCell(params);
    } catch {
      return asReactNode(raw);
    }
  }

  let cellValue: unknown = raw;
  if (col.valueGetter) {
    try {
      const vg = col.valueGetter as (
        v: unknown,
        r: T,
        c: GridColDef,
        api: unknown,
      ) => unknown;
      cellValue = vg(raw, row, col, stubApi);
    } catch {
      cellValue = raw;
    }
  }

  if (col.valueFormatter) {
    const fmt = col.valueFormatter as (a: unknown) => unknown;
    // Most portal columns use legacy `(rawValue) => …`; MUI Data Grid passes `GridValueFormatterParams` — try scalar first so cards match the grid.
    try {
      const plain = fmt(cellValue as never);
      if (plain !== undefined && plain !== null) return asReactNode(plain);
    } catch {
      /* try MUI params shape */
    }
    try {
      const withParams = fmt({
        value: cellValue,
        row,
        field: col.field,
        api: stubApi,
      } as never);
      if (withParams !== undefined && withParams !== null)
        return asReactNode(withParams);
    } catch {
      /* fall through */
    }
  }

  return asReactNode(cellValue);
}
